/**
 * Address text used by dashboard search and the geolocate hint.
 *
 * Street-type synonyms (`AV` / `Ave` / `Avenue`, `St` / `Street`, `Rd` / `Road`,
 * and the other Australian forms below) collapse to one token so a query and a
 * feed line can meet without either string being rewritten. Comparison is
 * case-insensitive and punctuation is not significant.
 *
 * Spelling tolerance is a bounded edit distance on the address tokens only, and
 * only once a token has already missed the cheap substring haystack. Identifiers
 * (accreditation codes, ServiceM8 numbers, postcodes) are never fuzzy-matched.
 */
/** Abbreviation and full word → one canonical token. Identity forms are omitted. */
const STREET_TYPE_CANONICAL = {
    av: 'ave',
    avenue: 'ave',
    street: 'st',
    str: 'st',
    road: 'rd',
    drive: 'dr',
    drv: 'dr',
    court: 'ct',
    crt: 'ct',
    place: 'pl',
    crescent: 'cres',
    cr: 'cres',
    crs: 'cres',
    circuit: 'cct',
    crct: 'cct',
    close: 'cl',
    parade: 'pde',
    terrace: 'tce',
    lane: 'ln',
    highway: 'hwy',
    grove: 'gr',
    boulevard: 'bvd',
    blvd: 'bvd',
    parkway: 'pwy',
    pkwy: 'pwy',
    esplanade: 'esp',
    square: 'sq',
    track: 'trk',
    trail: 'trl',
    freeway: 'fwy',
    gardens: 'gdns',
    heights: 'hts',
    ridge: 'rdge',
    alley: 'ally',
    arcade: 'arc',
    circle: 'cir',
    corner: 'cnr',
    promenade: 'prom',
    quay: 'qy',
    glen: 'gln',
    green: 'grn',
    view: 'vw',
    vista: 'vsta',
    entrance: 'ent',
    roadway: 'rdwy',
    walk: 'wk',
};
const TOKEN_SPLIT = /[^\p{L}\p{N}]+/u;
/** Lower-case tokens split on whitespace and punctuation. Empty pieces dropped. */
export function splitSearchTokens(value) {
    return value
        .toLowerCase()
        .split(TOKEN_SPLIT)
        .filter((token) => token.length > 0);
}
function canonicalStreetToken(token) {
    const lower = token.toLowerCase();
    return STREET_TYPE_CANONICAL[lower] ?? lower;
}
/**
 * The address folded for equality: case, punctuation and street-type synonyms
 * removed. "24 Mclaughlin AV" and "24 mclaughlin avenue" become the same string.
 */
export function normaliseAddressText(value) {
    return splitSearchTokens(value)
        .map((token) => canonicalStreetToken(token))
        .join(' ');
}
/**
 * True when `a` and `b` differ by at most `max` single-character edits.
 * Stops early once every way of reaching this column already exceeds `max`.
 */
function withinEditDistance(a, b, max) {
    if (a === b)
        return true;
    let source = a;
    let target = b;
    if (source.length > target.length) {
        source = b;
        target = a;
    }
    const sourceLength = source.length;
    const targetLength = target.length;
    if (targetLength - sourceLength > max)
        return false;
    let previous = new Uint16Array(sourceLength + 1);
    let current = new Uint16Array(sourceLength + 1);
    for (let i = 0; i <= sourceLength; i++)
        previous[i] = i;
    for (let column = 1; column <= targetLength; column++) {
        current[0] = column;
        let rowMin = column;
        const targetCode = target.charCodeAt(column - 1);
        for (let row = 1; row <= sourceLength; row++) {
            const cost = source.charCodeAt(row - 1) === targetCode ? 0 : 1;
            const deletion = previous[row] + 1;
            const insertion = current[row - 1] + 1;
            const substitution = previous[row - 1] + cost;
            const value = Math.min(deletion, insertion, substitution);
            current[row] = value;
            if (value < rowMin)
                rowMin = value;
        }
        if (rowMin > max)
            return false;
        const swap = previous;
        previous = current;
        current = swap;
    }
    return previous[sourceLength] <= max;
}
/**
 * A query token matches an address token when the street-type canonical forms
 * are equal, or — for alphabetic tokens of length at least 5 — the spelling is
 * within two edits (`McLaughlan` / `Mclaughlin`). Digits stay exact so a job
 * number one off does not fuzzy-match a street number.
 */
export function addressTokenMatches(token, addressTokens) {
    const needle = token.toLowerCase();
    const needleCanonical = canonicalStreetToken(needle);
    const fuzzy = needle.length >= 5 && !/\d/.test(needle);
    for (const raw of addressTokens) {
        const candidate = raw.toLowerCase();
        if (canonicalStreetToken(candidate) === needleCanonical)
            return true;
        if (fuzzy &&
            candidate.length >= 5 &&
            !/\d/.test(candidate) &&
            Math.abs(candidate.length - needle.length) <= 2 &&
            withinEditDistance(needle, candidate, 2)) {
            return true;
        }
    }
    return false;
}
//# sourceMappingURL=address-text.js.map